import { Injectable, Optional } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { TutorialCompletionState, TutorialState, TutorialStep } from './tutorial.model';
import { UserPreferencesService } from 'src/app/services/user-preferences.service';

export const TUTORIAL_STORAGE_PREFIX = 'centcom:tutorial';
export const ANONYMOUS_USERNAME = 'anonymous';

/**
 * TutorialService — Central Command In-UI Tutorial Framework
 *
 * Engineers register step arrays for their tool once (typically in ngOnInit).
 * The service handles auto-showing new tours, persisting completion state per
 * user + tool in localStorage, and broadcasting current state to the overlay.
 *
 * Quick-start:
 *
 *   constructor(private tutorialService: TutorialService) {}
 *
 *   ngOnInit() {
 *     this.tutorialService.register('my-tool', [
 *       { target: '#search-bar',    title: 'Search',  body: 'Filter results here.' },
 *       { target: '#results-table', title: 'Results', body: 'Click any row to expand.' },
 *     ]);
 *   }
 */
@Injectable({ providedIn: 'root' })
export class TutorialService {
  // ── Internal registry ───────────────────────────────────────────────────────

  /** Maps toolId → registered steps. */
  private readonly registry = new Map<string, TutorialStep[]>();

  // ── State stream ────────────────────────────────────────────────────────────

  private readonly _state$ = new BehaviorSubject<TutorialState | null>(null);

  /** Observable emitting the current tutorial state (null when no tour is active). */
  readonly tutorialState$: Observable<TutorialState | null> =
    this._state$.asObservable();

  // ── localStorage helpers ────────────────────────────────────────────────────

  private storageKey(toolId: string, username?: string): string {
    const user = username || this.getUsername();
    return TutorialService.buildStorageKey(toolId, user);
  }

  static getStoragePrefix(username: string): string {
    return `${TUTORIAL_STORAGE_PREFIX}:${username}:`;
  }

  static getToolIdFromStorageKey(username: string, storageKey: string | null): string | null {
    const prefix = TutorialService.getStoragePrefix(username);
    if (storageKey !== null && storageKey.startsWith(prefix)) {
      return storageKey.slice(prefix.length);
    }
    return null;
  }

  static buildStorageKey(toolId: string, username: string): string {
    return `${this.getStoragePrefix(username)}${toolId}`;
  }

    /**
   * Writes tutorial completion flags back into localStorage so that
   * `TutorialService.hasCompleted()` continues to work without any changes.
   */
  static writeTutorialCompletions(
    username: string,
    completions: TutorialCompletionState,
  ): void {
    try {
      for (const [toolId, done] of Object.entries(completions)) {
          localStorage.setItem(this.buildStorageKey(toolId, username), done.toString());
      }
    } catch {
      // fail silently
    }
  }

  static isComplete(value: string | null): boolean {
    return value !== null && value !== undefined && parseInt(value, 10) <= Date.now();
  }

  /**
   * Returns true if this user has already completed or skipped the tour for
   * the given toolId.
   */
  hasCompleted(toolId: string): boolean {
    try {
      const value = localStorage.getItem(this.storageKey(toolId));
      return TutorialService.isComplete(value);
    } catch {
      return false;
    }
  }

  /** Marks a tour as completed for the current user. */
  markCompleted(toolId: string): void {
    try {
      localStorage.setItem(this.storageKey(toolId), Date.now().toString());
    } catch {
      console.warn(`[TutorialService] Unable to save tutorial completion for toolId '${toolId}' to local storage.`);
    }
    // Persist the updated completion state to the backend.
    if (this.userPreferencesService) {
      this.userPreferencesService.saveToBackend();
    } else {
      console.warn('[TutorialService] No UserPreferencesService available — cannot persist tutorial completion to backend.');
    }
  }

  /**
   * Clears the completion flag for a tool so that the tour will auto-show
   * again on the user's next visit. Useful for resetting from preferences UI.
   */
  reset(toolId: string): void {
    try {
      localStorage.removeItem(this.storageKey(toolId));
    } catch {
      // fail silently
    }
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  constructor(
    private readonly msalService: MsalService,
    @Optional() private readonly userPreferencesService: UserPreferencesService | null,
  ) {}

  /**
   * Registers a set of walkthrough steps for a tool and auto-starts the tour
   * if this user has not yet seen it.
   *
   * Call this from a component's ngOnInit. The tour will begin after a single
   * animation frame to ensure the DOM elements referenced in `target` exist.
   *
   * @param toolId  Unique, stable identifier for the tool (e.g. 'account-search').
   * @param steps   Ordered array of TutorialStep definitions.
   */
  register(toolId: string, steps: TutorialStep[]): void {
    if (!steps || steps.length === 0) {
      console.warn(`[TutorialService] register('${toolId}') called with empty steps — skipping.`);
      return;
    }

    if (this.registry.has(toolId)) {
      console.warn(
        `[TutorialService] register('${toolId}') — a tour with this ID is already registered. ` +
        `Each tool must use a unique toolId. Skipping duplicate registration.`,
      );
      return;
    }

    this.registry.set(toolId, steps);

    if (!this.hasCompleted(toolId)) {
      // Defer by one frame so the host component's template has rendered.
      requestAnimationFrame(() => this.start(toolId));
    }
  }

  /**
   * Explicitly launches the tour for a given tool (e.g., from a "Show Tour" button).
   * No-op if the toolId has not been registered.
   */
  start(toolId: string): void {
    const steps = this.registry.get(toolId);
    if (!steps || steps.length === 0) {
      console.warn(`[TutorialService] start('${toolId}') — tool not registered or has no steps.`);
      return;
    }

    this._state$.next({ toolId, steps, currentStepIndex: 0, isActive: true });
    this.runBeforeShow(steps[0]);
  }

  /** Advance to the next step, or end the tour if on the last step. */
  next(): void {
    const state = this._state$.getValue();
    if (!state) {return;}

    const nextIndex = state.currentStepIndex + 1;
    if (nextIndex >= state.steps.length) {
      this.end();
    } else {
      this.runBeforeShow(state.steps[nextIndex]);
      this._state$.next({ ...state, currentStepIndex: nextIndex });
    }
  }

  /** Go back to the previous step. No-op on the first step. */
  prev(): void {
    const state = this._state$.getValue();
    if (!state || state.currentStepIndex === 0) {return;}

    const prevIndex = state.currentStepIndex - 1;
    this.runBeforeShow(state.steps[prevIndex]);
    this._state$.next({ ...state, currentStepIndex: prevIndex });
  }

  /**
   * Dismiss the tour without marking it completed.
   * The tour will not auto-show again this session but will auto-show on the
   * user's next visit if `markCompleted` was not called.
   * The overlay calls this when the user clicks "Skip".
   */
  skip(): void {
    const state = this._state$.getValue();
    if (state) {
      this.markCompleted(state.toolId);
    }
    this._state$.next(null);
  }

  /**
   * Complete the tour (called when user clicks "Done" on the last step).
   * Marks as completed in localStorage so it won't auto-show again.
   */
  end(): void {
    const state = this._state$.getValue();
    if (state) {
      this.markCompleted(state.toolId);
    }
    this._state$.next(null);
  }

  // ── Internal helpers ────────────────────────────────────────────────────────

  private getUsername(): string {
    try {
      const accounts = this.msalService.instance.getAllAccounts();
      return accounts[0]?.username ?? ANONYMOUS_USERNAME;
    } catch {
      return ANONYMOUS_USERNAME;
    }
  }

  private runBeforeShow(step: TutorialStep): void {
    if (step.beforeShow) {
      try {
        step.beforeShow();
      } catch (e) {
        console.error('[TutorialService] beforeShow hook threw an error:', e);
      }
    }
  }
}
